import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../hooks/useAuth";
import { useNavigate } from "../../../lib/router";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import attendanceService from "../../../services/attendance.service";
import {
  formatAttendanceDateTime,
  formatAttendanceMinutes,
  getAttendanceStatusClass,
  getAttendanceStatusLabel,
} from "../../../utils/attendance";

export function AttendanceTodaySummaryCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageTeam =
    user?.role === "admin" || user?.role === "hr" || user?.role === "manager";

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["attendance", "today"],
    queryFn: () => attendanceService.getToday(),
    enabled: !!user,
    retry: false,
  });

  return (
    <Card className="border-sky-100 dark:border-sky-900 shadow-md hover:shadow-lg transition-shadow">
      <CardHeader className="bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-950 dark:to-cyan-950 border-b dark:border-gray-700">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-xl">🏠</span>
              WFH Attendance วันนี้
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              ยืนยันเข้างาน-ออกงานพร้อมพิกัดและหมายเหตุ
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/attendance/wfh")}>
              เปิดหน้า WFH
            </Button>
            {canManageTeam && (
              <Button
                variant="outline"
                onClick={() => navigate("/attendance/manage")}
              >
                จัดการทีม
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {isLoading && (
          <div className="text-sm text-gray-600 dark:text-gray-300">
            กำลังโหลดสถานะ WFH...
          </div>
        )}

        {isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            {((error as any)?.response?.data?.error?.message ||
              (error as Error)?.message ||
              "ไม่สามารถโหลดสถานะ WFH ได้") as string}
          </div>
        )}

        {!isLoading && !isError && data && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  วันที่ทำงาน
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {data.workDate}
                </p>
              </div>
              <span
                className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-sm font-medium ${getAttendanceStatusClass(
                  data.record?.status
                )}`}
              >
                {getAttendanceStatusLabel(data.record?.status)}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  เวลามาตรฐาน
                </p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                  {data.schedule.startTime || "-"} - {data.schedule.endTime || "-"}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {data.schedule.source} / {data.schedule.timezone}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  เข้างาน
                </p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                  {formatAttendanceDateTime(data.record?.checkInAt)}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  ออกงาน
                </p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                  {formatAttendanceDateTime(data.record?.checkOutAt)}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  เวลาทำงาน / มาสาย
                </p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                  {formatAttendanceMinutes(data.record?.workedMinutes)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  มาสาย {formatAttendanceMinutes(data.record?.lateMinutes ?? 0)}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AttendanceTodaySummaryCard;
